import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as documentService from '../services/documentService';

/**
 * Hook para buscar documentos do usuário com cache automático
 * 
 * Benefícios:
 * - Cache de 5 minutos (não refaz requisição desnecessária)
 * - Refetch automático em background
 * - Loading state gerenciado automaticamente
 */
export function useDocuments() {
  return useQuery({
    queryKey: ['documents'],
    queryFn: documentService.getUserDocuments,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

/**
 * Hook para buscar um documento específico
 */
export function useDocument(documentId: string | undefined) {
  return useQuery({
    queryKey: ['documents', documentId],
    queryFn: () => documentId ? documentService.getDocument(documentId) : Promise.reject('No ID'),
    enabled: !!documentId, // Só executa se tiver ID
    staleTime: 10 * 60 * 1000, // 10 minutos para documento específico
  });
}

/**
 * Hook para criar documento com invalidação automática do cache
 */
export function useCreateDocument() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { templateId: string; variables: Record<string, string>; formattedTitle?: string }) => 
      documentService.generateDocument(data.templateId, data.variables, data.formattedTitle),
    onSuccess: () => {
      // Invalida cache para recarregar lista
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

/**
 * Hook para deletar documento
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (documentId: string) => documentService.deleteDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

/**
 * Hook para buscar pastas
 */
export function useFolders() {
  return useQuery({
    queryKey: ['folders'],
    queryFn: documentService.getUserFolders,
    staleTime: 10 * 60 * 1000, // 10 minutos
  });
}





